import { Col, Container, Row } from 'react-bootstrap';
import { db, useAuth } from '../auth/firebase-auth';
import { query, where } from 'firebase/firestore';
import { Login } from './login';
import ConnectedAccounts from '../components/connected-accounts/connected-accounts';
import { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import NextVideo from '../components/next-video/next-video';

interface AccessTokenResponse {
  expires_in: number;
  refresh_expires_in: number;
  token_type: string;
  open_id: string;
  scope: string;
  refresh_token: string;
  access_token: string;
}

interface UserDataDetails {
  stitch_disabled: boolean;
  creator_username: string;
  comment_disabled: boolean;
  creator_avatar_url: string;
  duet_disabled: boolean;
  privacy_level_options: string[];
  creator_nickname: string;
  max_video_post_duration_sec: number;
}

interface ConnectedAccount {
  tiktok?: {
    connected_social_media_account: string;
    account_data: {
      access_token_data: AccessTokenResponse;
      user_data: UserDataDetails;
    };
  };
}

interface TikTokData {
  connected_accounts?: ConnectedAccount;
  account_status: string;
  user_uid: string;
  tiktok_account_category: string;
  email: string;
}

export interface TikTokAccountRecord extends TikTokData {
  id: string;
}

function useFirestoreQuery(email?: string, refreshKey = 0) {
  const [profileData, setProfileData] = useState<TikTokAccountRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!email) {
        setProfileData([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const q = query(
          collection(db, 'tiktok_automation_users'),
          where('email', '==', email)
        );
        const querySnapshot = await getDocs(q);
        const newData: TikTokAccountRecord[] = [];
        querySnapshot.forEach((doc) => {
          newData.push({
            id: doc.id,
            ...(doc.data() as TikTokData)
          });
        });
        setProfileData(newData);
      } catch (errorValue) {
        setError(errorValue as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [email, refreshKey]);

  return { profileData, loading, error };
}

/* Home page component */
export const Home = () => {
  const { currentUser } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const { profileData, loading, error } = useFirestoreQuery(
    currentUser?.email,
    refreshKey
  );

  const activeAccounts = profileData.filter((account) => {
    const accessToken =
      account.connected_accounts?.tiktok?.account_data?.access_token_data
        ?.access_token;
    return account.account_status === 'active' && Boolean(accessToken);
  });
  const isTikTokAccountConnected = activeAccounts.length > 0;

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <>
      {currentUser ? (
        <Container>
          <Row>
            <Col>
              <div className="p-2">
                <h2>Dashboard</h2>
                <p>Social media accounts</p>
                <ConnectedAccounts
                  accounts={profileData}
                  onAccountChanged={() => setRefreshKey((value) => value + 1)}
                />
              </div>
            </Col>
            <Col>
              <NextVideo isAccountConnected={isTikTokAccountConnected} />
            </Col>
          </Row>
        </Container>
      ) : (
        <Login />
      )}
    </>
  );
};
